import {
  Badge,
  BadgeProps,
  Body1,
  Body2,
  Button,
  Card,
  CardFooter,
  CardHeader,
  CardPreview,
  makeStyles,
  Subtitle1,
  Subtitle2,
  Subtitle2Stronger,
  Title1,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { FC, ReactNode } from 'react';

/**
 * Reusable card component for the top “stat” cards.
 */
type StatCardProps = {
  /** Title displayed in CardHeader (e.g. "Submission Readiness") */
  title: string;
  /** Tooltip text displayed when hovering over the info button */
  tooltip: string;
  /** Children shown inside the CardPreview area (can be a number, circle progress, etc.) */
  preview: ReactNode;
  /** Icon color for the info button */
  infoButtonColor?: string;
  /** Icon displayed at left side of footer badge */
  footerBadgeIcon: ReactNode;
  /** Main text displayed inside the footer badge */
  footerBadgeText: string;
  /** Subtitle text displayed to the right of the badge */
  footerSubtitle: string;
  /** Optional Tailwind/fluent class override for the badge so you can adjust spacing */
  footerBadgeClassName?: string;
  /** Color of the badge */
  badgeColor: BadgeProps['color'];
};

const useStyles = makeStyles({
  card: {
    // Some basic style for the card
    display: 'flex',
    width: '25%',
    flexDirection: 'column',
    alignItems: 'between',
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHeaderContainer: {
    display: 'flex',
    height: 'fit-content',
  },
  cardPreviewContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  cardFooterContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  // cardSubtitle: {
  //   color: tokens.colorNeutralForeground3,
  // },
  // cardBigNumber: {
  //   color: tokens.colorBrandForeground1,
  //   fontSize: '3rem',
  // },
  cardBigNumberContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
  },
});

const StatCard: FC<StatCardProps> = ({
  title,
  tooltip,
  preview,
  infoButtonColor = 'green',
  footerBadgeIcon,
  footerBadgeText,
  footerSubtitle,
  badgeColor,
}) => {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.cardHeaderContainer}
        header={
          <div className="flex justify-between items-center w-full">
            <Subtitle2Stronger>{title}</Subtitle2Stronger>
            <Tooltip withArrow content={tooltip} relationship="description">
              <Button size="large" icon={<InfoRegular color={infoButtonColor} />} appearance="transparent" />
            </Tooltip>
          </div>
        }
      />
      <CardPreview className={classes.cardPreviewContainer}>{preview}</CardPreview>
      <CardFooter className={classes.cardFooterContainer}>
        <div className="flex items-center justify-center gap-1">
          <Badge appearance="tint" shape="rounded" color={badgeColor}>
            {footerBadgeIcon}
            <span className="font-light">{footerBadgeText}</span>
          </Badge>
          <Body2 className="text-neutralFg-3">{footerSubtitle}</Body2>
        </div>
      </CardFooter>
    </Card>
  );
};

type TPropsNumberPreview = {
  value: number;
  subtitle: string;
  colorSchemeKey: 'info' | 'danger';
};

/**
 * Simple helper to render the big numeric preview
 * used by the "Critical issues" and "Open approvals" cards.
 */
export const NumberPreview: FC<TPropsNumberPreview> = ({ value, subtitle, colorSchemeKey }) => {
  const classes = useStyles();

  const colorScheme = {
    info: {
      title: 'text-brand-80',
      subtitle: 'text-neutralFg-3',
      border: 'border-brand-160',
    },
    danger: {
      title: 'text-[#d13438]', // colorPaletteRedForeground3 in fluent ui from data.txt
      subtitle: 'text-neutralFg-3',
      border: 'border-[#f1bbbc]', // colorPaletteRedBorder1 in fluent ui from data.txt
    },
  };

  return (
    <div
      className={`!h-40 !w-40 rounded-md bg-neutralBg-2 flex items-center justify-center p-4 border-4 ${colorScheme[colorSchemeKey].border}`}
    >
      <div className={classes.cardBigNumberContainer}>
        <Title1 as="span" className={`!text-5xl ${colorScheme[colorSchemeKey].title}`}>
          {value}
        </Title1>
        <Body1 className={colorScheme[colorSchemeKey].subtitle}>{subtitle}</Body1>
      </div>
    </div>
  );
};

export default StatCard;
